/**
 * Wire protocol between the canvas client (apps/web) and a browser worker
 * (apps/browser). Mirrors apps/browser/src/protocol.ts + the exported types of
 * its session.ts, which is exactly why it lives here: this is a shape that
 * crosses the network, so it gets one definition both halves import rather than
 * two copies that drift (CLAUDE.md > Writing code).
 *
 * `apps/browser` still declares its own copy today — it ships as a standalone
 * ESM service with no build-time link to @repo/contracts. When it is folded into
 * the workspace build, delete its protocol.ts types and re-export these.
 *
 * Everything travels over one WebSocket: with Cloud Run `--concurrency=1` the
 * instance holding a session's socket has no free capacity, so any *extra* HTTP
 * request is guaranteed to land on a different instance with no browser. The
 * socket is the session affinity.
 */

/**
 * The short-lived credential a signed-in user exchanges their session for
 * before opening a browser worker socket.
 *
 * The worker (apps/browser) is reachable without Google IAM auth — a browser
 * cannot mint an identity token for an end user, and proxying frames through
 * the control plane would double egress and put it in the data path. So this
 * ticket is what stands between the internet and a fleet of remote-controlled
 * browsers: a JWT signed by apps/api with the secret the worker verifies
 * (`CANVAS_WORKER_JWT_SECRET` on both sides), scoped to one user and one
 * session, expiring in minutes.
 *
 * It is deliberately NOT the app's own session token: that one lives in
 * localStorage for 14 days, and the worker has no way to check it.
 */
export interface BrowserTicket {
  /** JWT with `{ sub: <uid>, sid: <sessionId> }`. Goes on the socket as `?token=`. */
  token: string;

  /** The `sid` claim, echoed so the client can log/correlate a session. */
  sessionId: string;

  /** Epoch ms. The client refuses to open a socket with an expired ticket. */
  expiresAt: number;

  /**
   * Where to open the socket. Either an absolute ws(s):// origin or a
   * same-origin path (the dev default, `/browser`, which Vite proxies to the
   * worker container) — the client resolves a path against window.location.
   */
  workerUrl: string;
}

// ── Session shapes ───────────────────────────────────────────────────────────

export type BrowserCloseReason =
  | 'client_request'
  | 'idle_timeout'
  | 'max_session'
  | 'browser_crash'
  | 'shutdown';

export interface BrowserViewport {
  width: number;
  height: number;
  mobile: boolean;
}

export interface BrowserSessionInfo {
  id: string;
  createdAt: number;
  expiresAt: number;
  url: string;
  viewport: BrowserViewport;
  viewers: number;
  idleFor: number;
  /** Frames the worker's maxFps cap suppressed over the session's life. */
  framesDropped: number;
  /** False while the client's live view is hidden — the screencast is paused. */
  visible: boolean;
}

/** One extracted copy element, keyed so a finding can box the exact node. */
export interface CopyItem {
  id: string;
  kind: string;
  text: string;
  /** "x,y WxH" in page-absolute CSS pixels. */
  rect: string;
}

export interface UsabilityItem {
  id: string;
  tag: string;
  role: string;
  label: string;
  rect: string;
  icon: boolean;
  /**
   * What a native `<select>` offers, in order.
   *
   * **This is not a masking hole, and the next reader will think it is.** The
   * rule everywhere else is that the walker is told what is on screen and never
   * where anything leads — but a `<select>`'s options are values, not
   * destinations, and a real first-time user reads all of them the instant they
   * press the control. The walker cannot, and the reason is entirely ours: the
   * popup a native select opens is a browser widget outside the page, so
   * `Page.captureScreenshot` does not contain it and `Input.dispatchMouseEvent`
   * cannot hit it. Listing them here restores what a person already has rather
   * than granting anything extra.
   *
   * Capped, because a country select is 200 entries and a snapshot holds 200
   * items — the `.dom.json` beside every capture is read by audits too, and
   * Firestore's 1 MiB is not a budget to spend on the same list of countries
   * once per form. `optionsTruncated` says the list is what was found rather
   * than all there is, the same honesty `truncated.usability` owes the walker.
   */
  options?: string[];
  optionsTruncated?: boolean;
}

export interface DomSnapshot {
  url: string;
  title: string;
  /** Page-absolute origin of the capture; aligns the rects below to the image. */
  originX: number;
  originY: number;
  pageWidth: number;
  pageHeight: number;
  copyItems: CopyItem[];
  usabilityItems: UsabilityItem[];
  truncated: { copy: boolean; usability: boolean };
}

export interface CaptureBundle {
  /** base64 PNG, taken from the page — never from the lossy screencast. */
  image: string;
  /** CSS pixels. Same coordinate space as the DOM snapshot's rects. */
  imgW: number;
  imgH: number;
  /** Actual image pixels — imgW * dpr. Never rect coordinates. */
  pixelW: number;
  pixelH: number;
  dpr: number;
  fullPage: boolean;
  capturedAt: number;
  dom: DomSnapshot | null;
}

/**
 * What every stitch step reports back — everything the client needs to lay the
 * tiles of a scroll-and-stitch capture out, measured live by the worker.
 * Mirrors `StitchState` in apps/browser/src/stitch.ts.
 */
export interface BrowserStitchState {
  /** Where the scroller actually is, which a page may clamp or snap. */
  actualY: number;
  maxScroll: number;
  /** The scroller's own scrollHeight. */
  contentHeight: number;
  /** The whole picture: the scroller's content plus anything above and below
   *  it in the viewport. Equal to contentHeight when the window scrolls. */
  totalHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  /** The scroller's box in the viewport — 0 and viewportHeight for the window. */
  containerTop: number;
  containerBottom: number;
  /** The page's own background, for any band no tile covers. Only
   *  `stitch-prepare` fills it; later steps send ''. */
  background: string;
  /** The window has more page than it shows but will not scroll to it — a
   *  scroll-jacked page, or a lock a dialog left. `stitch-prepare` and
   *  `stitch-unlock` only. */
  locked?: boolean;
  /** `stitch-wheel` only: whether the wheel changed what is on screen. */
  moved?: boolean;
}

// ── Client → worker ──────────────────────────────────────────────────────────

export interface BrowserInputMessage {
  type: 'mousedown' | 'mouseup' | 'mousemove' | 'scroll' | 'keydown' | 'keyup';
  x?: number;
  y?: number;
  deltaX?: number;
  deltaY?: number;
  key?: string;
  code?: string;
  keyCode?: number;
  modifiers?: number;
}

export interface BrowserNavigateMessage {
  type: 'navigate';
  url?: string;
  action?: 'back' | 'forward' | 'reload';
}

export interface BrowserViewportMessage {
  type: 'viewport';
  width: number;
  height: number;
  mobile?: boolean;
}

export interface BrowserCaptureMessage {
  type: 'capture';
  fullPage?: boolean;
  includeDom?: boolean;
  /** Echoed on the matching `captured`/`error` reply — a socket has no
   *  request/response pairing of its own. */
  requestId?: string;
}

/**
 * Set a native `<select>` to one of its options.
 *
 * Its own message rather than an input event, because a native select is the
 * one control that cannot be driven by pointer events at all: pressing it opens
 * a browser widget outside the page, which no screenshot contains and no
 * synthesized click can reach. The worker resolves the element at the point and
 * sets it — no selector crosses the wire here either.
 */
export interface BrowserSelectMessage {
  type: 'select';
  x: number;
  y: number;
  option: string;
}

/** Whether the live view is on screen. Hidden ⇒ the worker stops encoding
 *  frames the client would throw away. */
export interface BrowserVisibilityMessage {
  type: 'visible';
  visible: boolean;
}

/**
 * The page-side steps of a scroll-and-stitch full-page capture.
 * `stitch-prepare` readies the page (capture stylesheet, lazy-load walk) and
 * returns to the top; `stitch-scroll` moves to `y` and neutralises
 * fixed/sticky elements; `stitch-finish` measures and restores. Each answers
 * with a `stitch` message echoing `requestId`. Tiles are ordinary `capture`s.
 */
export interface BrowserStitchMessage {
  type:
    | 'stitch-finish'
    | 'stitch-prepare'
    | 'stitch-scroll'
    | 'stitch-unlock'
    | 'stitch-wheel';
  /** Only for `stitch-scroll`. */
  y?: number;
  requestId: string;
}

export type BrowserClientMessage =
  | BrowserStitchMessage
  | BrowserInputMessage
  | BrowserNavigateMessage
  | BrowserViewportMessage
  | BrowserCaptureMessage
  | BrowserSelectMessage
  | BrowserVisibilityMessage;

// ── Worker → client ──────────────────────────────────────────────────────────

export type BrowserServerMessage =
  /** First message on every connection. Commands sent before it get an
   *  `error` — Chromium takes ~1s to launch. */
  | { type: 'ready'; protocolVersion: number; session: BrowserSessionInfo }
  | { type: 'frame'; data: string }
  | { type: 'url'; url: string }
  | { type: 'viewport'; viewport: BrowserViewport }
  | { type: 'captured'; requestId?: string; bundle: CaptureBundle }
  | { type: 'stitch'; requestId: string; state: BrowserStitchState }
  /** Never ends the session: a blocked URL or bad viewport is reported and the
   *  browser keeps running. */
  | { type: 'error'; requestId?: string; message: string }
  | { type: 'closed'; reason: BrowserCloseReason };

export const BROWSER_PROTOCOL_VERSION = 3;

/** Close codes, so a client can tell "try again" from "stop". */
export const BROWSER_CLOSE_UNAUTHORIZED = 4401;
/** This worker already owns a session — get a different instance, don't queue. */
export const BROWSER_CLOSE_BUSY = 4409;
export const BROWSER_CLOSE_SESSION_ENDED = 4410;
